import { useState, useEffect, useRef } from 'react'
import { Check, ChatText, BookOpen } from '@phosphor-icons/react'

export interface Scenario {
  id: string
  title: string
  practicePrompt: string
  correctResponse: string
  facilitatorNotes?: string
  roles: string[]
  stage: string
  difficulty?: string
}

export interface ParticipantResponse {
  userId: string
  userName: string
  responseText?: string
  respondedAt: string
}

export interface ScenarioSession {
  id: string
  scenarioId: string
  status: 'waiting' | 'active' | 'revealed' | 'closed'
  responses: ParticipantResponse[]
}

// Trainee View - participates in facilitated rehearsal
export function ScenarioTraineeView({
  sessionId,
  scenario,
  onSubmit,
  hasResponded = false,
}: {
  sessionId: string
  scenario: Scenario
  onSubmit: (responseText?: string) => Promise<void>
  hasResponded?: boolean
}) {
  void sessionId
  const [responseText, setResponseText] = useState('')
  const [submitted, setSubmitted] = useState(hasResponded)
  const [isPending, setIsPending] = useState(false)

  const handleSubmit = async () => {
    setIsPending(true)
    try {
      await onSubmit(responseText.trim() || undefined)
      setSubmitted(true)
    } catch (err) {
      console.error('Failed to submit:', err)
    } finally {
      setIsPending(false)
    }
  }

  return (
    <div className="scenario-trainee">
      <div className="scenario-header">
        <span className="scenario-eyebrow">Rehearsal Scenario</span>
        <h2>{scenario.title}</h2>
      </div>

      <div className="scenario-prompt">
        <div className="scenario-prompt__label">
          <BookOpen size={18} weight="bold" />
          <span>Situation</span>
        </div>
        <p className="scenario-prompt__text">{scenario.practicePrompt}</p>
      </div>

      {!submitted ? (
        <div className="scenario-response-area">
          <label htmlFor="response-text" className="scenario-response__label">
            <ChatText size={16} weight="bold" />
            <span>Your response (optional)</span>
          </label>
          <textarea
            id="response-text"
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            placeholder="Type your response, or just click 'I've Responded' if discussing verbally..."
            className="scenario-response__textarea"
            rows={3}
          />
          <button
            type="button"
            disabled={isPending}
            onClick={handleSubmit}
            className="scenario-submit-btn"
          >
            {isPending ? 'Submitting…' : "I've Responded"}
          </button>
        </div>
      ) : (
        <div className="scenario-submitted-confirmation">
          <Check size={20} weight="bold" />
          <div>
            <p>Your response has been submitted</p>
            <p className="scenario-submitted__hint">The facilitator will reveal the correct response when everyone is ready.</p>
          </div>
        </div>
      )}
    </div>
  )
}

// Facilitator Dashboard - controls the session and sees responses
export function ScenarioFacilitatorDashboard({
  scenario,
  session,
  onReveal,
  onNext,
}: {
  scenario: Scenario
  session: ScenarioSession
  onReveal: () => void
  onNext: () => void
}) {
  const responseCount = session.responses.length

  return (
    <div className="scenario-facilitator">
      <div className="scenario-facilitator__header">
        <div>
          <span className="scenario-eyebrow">Facilitator Dashboard</span>
          <h2>{scenario.title}</h2>
        </div>
        <div className="scenario-facilitator__status">
          <span className={`scenario-status scenario-status--${session.status}`}>
            {session.status === 'waiting' && 'Waiting'}
            {session.status === 'active' && 'Collecting Responses'}
            {session.status === 'revealed' && 'Revealed'}
            {session.status === 'closed' && 'Closed'}
          </span>
          <span className="scenario-response-count">{responseCount} responded</span>
        </div>
      </div>

      <div className="scenario-prompt">
        <div className="scenario-prompt__label">
          <BookOpen size={18} weight="bold" />
          <span>Practice Prompt</span>
        </div>
        <p className="scenario-prompt__text">{scenario.practicePrompt}</p>
      </div>

      {session.status === 'revealed' && (
        <div className="scenario-reveal">
          <div className="scenario-correct-response">
            <h3>Correct Response</h3>
            <p>{scenario.correctResponse}</p>
          </div>

          {scenario.facilitatorNotes && (
            <div className="scenario-facilitator-notes">
              <h4>Facilitator Notes</h4>
              <p>{scenario.facilitatorNotes}</p>
            </div>
          )}
        </div>
      )}

      <div className="scenario-results">
        <h3>Participant Responses ({session.responses.length})</h3>
        {session.responses.length === 0 ? (
          <p className="scenario-no-responses">No responses yet. Trainees will appear here when they respond.</p>
        ) : (
          <ul className="scenario-participants">
            {session.responses.map((response, i) => (
              <li key={i} className="scenario-participant">
                <span className="scenario-participant__name">{response.userName}</span>
                {response.responseText && (
                  <span className="scenario-participant__choice">{response.responseText}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="scenario-facilitator__actions">
        {session.status === 'active' && (
          <button onClick={onReveal} className="btn btn-primary">
            Reveal Correct Response
          </button>
        )}
        {session.status === 'revealed' && (
          <button onClick={onNext} className="btn btn-primary">
            Next Scenario
          </button>
        )}
      </div>
    </div>
  )
}

// Hook for SSE connection
export function useScenarioSSE(sessionId: string) {
  const [session, setSession] = useState<ScenarioSession | null>(null)
  const [error, setError] = useState<string | null>(null)
  const eventSourceRef = useRef<EventSource | null>(null)

  useEffect(() => {
    if (!sessionId) return

    const es = new EventSource(`/api/practice/${sessionId}/events`)
    eventSourceRef.current = es

    es.addEventListener('scenario', (e) => {
      const data = JSON.parse(e.data)
      setSession(data.payload)
    })

    es.addEventListener('error', () => {
      setError('Connection lost. Reconnecting…')
    })

    return () => {
      es.close()
    }
  }, [sessionId])

  return { session, error }
}
